import { useLayoutEffect, useRef, useState } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';

import RoleAssignmentIndicator from '../../../presentations/RoleAssignmentIndicator';

const ROLE_NODE_SIZE_CONFIG = {
  scaleMultiplierBySize: {
    lg: 1.45,
    md: 1.225,
    sm: 1,
    xs: 0.75,
  },
  assigned: {
    innerDiameterPx: 12.98,
    outerDiameterPx: 21,
  },
  unassigned: {
    innerDiameterPx: 11,
    outerDiameterPx: 16,
  },
} as const;

const SHELL_BORDER_WIDTH_PX = 1;
const EXPANDED_PILL_EDGE_MARGIN_PX = 12;

function getScaledDiameterPx(diameterPx: number, scaleMultiplier: number) {
  return diameterPx * scaleMultiplier;
}

function getHorizontalClippingBounds(element: HTMLElement): {
  left: number;
  right: number;
} {
  let left = 0;
  let right = window.innerWidth;
  let currentElement: HTMLElement | null = element.parentElement;

  while (currentElement) {
    const computedStyle = window.getComputedStyle(currentElement);
    if (clipsHorizontalOverflow(computedStyle.overflowX, computedStyle.overflowY)) {
      const rect = currentElement.getBoundingClientRect();
      left = Math.max(left, rect.left);
      right = Math.min(right, rect.right);
    }
    currentElement = currentElement.parentElement;
  }

  return { left, right };
}

function clipsHorizontalOverflow(
  overflowX: string,
  overflowY: string,
): boolean {
  return overflowX !== 'visible' || overflowY !== 'visible';
}

interface DiagramRoleNodeProps {
  roleName: string;
  fullLabel: string;
  boneName: string | null;
  confidence: number | null;
  x: number;
  y: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isActive: boolean;
  isPending: boolean;
  isAssignedToSelectedBone: boolean;
  assignmentEnabled: boolean;
  onClick: () => void;
  onHoverChange?: (roleName: string | null) => void;
  onClear?: () => void;
}

export default function DiagramRoleNode({
  roleName,
  fullLabel,
  boneName,
  confidence,
  x,
  y,
  size = 'sm',
  isActive,
  isPending,
  isAssignedToSelectedBone,
  assignmentEnabled,
  onClick,
  onHoverChange,
  onClear,
}: DiagramRoleNodeProps) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const isAssigned = boneName !== null;
  const isExpanded = isActive || isPending;
  const [isHovered, setIsHovered] = useState(false);
  const [expandDirection, setExpandDirection] = useState<'left' | 'right'>(
    'right',
  );
  const scaleMultiplier = ROLE_NODE_SIZE_CONFIG.scaleMultiplierBySize[size];
  const nodeSizeConfig = isAssigned
    ? ROLE_NODE_SIZE_CONFIG.assigned
    : ROLE_NODE_SIZE_CONFIG.unassigned;
  const innerIndicatorDiameterPx = getScaledDiameterPx(
    nodeSizeConfig.innerDiameterPx,
    scaleMultiplier,
  );
  const outerIndicatorDiameterPx = getScaledDiameterPx(
    nodeSizeConfig.outerDiameterPx,
    scaleMultiplier,
  );
  const labelMaxWidthClassName =
    size === 'lg'
      ? 'max-w-[140px]'
      : size === 'md'
        ? 'max-w-[129px]'
        : size === 'sm'
          ? 'max-w-[118px]'
          : 'max-w-[104px]';
  const clearVisible = Boolean(isActive && isAssigned && onClear);
  const shellClassName = isPending
    ? 'border-blue-50/90 bg-white/95 dark:border-blue-50/60 dark:bg-gray-75/95'
    : isAssignedToSelectedBone
      ? 'border-green-35/90 bg-white/95 dark:border-green-35/65 dark:bg-gray-75/95'
      : isActive
        ? 'border-blue-50/75 bg-white/95 dark:border-blue-50/45 dark:bg-gray-75/95'
        : isAssigned
          ? 'border-white/25 bg-white/92 dark:border-gray-60/35 dark:bg-gray-75/92'
          : 'border-gray-30/80 bg-white/88 dark:border-gray-60/35 dark:bg-gray-75/88';
  const tooltipText = isPending
    ? `${fullLabel}: click a bone to assign`
    : boneName
      ? `${fullLabel}: assigned to ${boneName}`
      : `${fullLabel}: unassigned`;
  // Keep the diagram anchor on the indicator center. In the collapsed state
  // the shell hugs the outer circle, with only the shell border extending it.
  const leftOffsetPx = outerIndicatorDiameterPx / 2 + SHELL_BORDER_WIDTH_PX;
  const showLabel = isExpanded || isHovered;
  const expandsLeft = showLabel && expandDirection === 'left';
  const shellTransform = expandsLeft
    ? `translateX(calc(-100% + ${leftOffsetPx}px))`
    : `translateX(-${leftOffsetPx}px)`;

  useLayoutEffect(() => {
    if (!showLabel) {
      setExpandDirection('right');
      return;
    }

    const updateExpandDirection = () => {
      const shellElement = shellRef.current;
      const parentElement = shellElement?.offsetParent;
      if (!(parentElement instanceof HTMLElement) || !shellElement) return;

      const shellWidthPx = shellElement.offsetWidth;
      const parentRect = parentElement.getBoundingClientRect();
      const clippingBounds = getHorizontalClippingBounds(shellElement);
      const anchorXPx = (parentElement.clientWidth * x) / 100;
      const anchorViewportXPx = parentRect.left + anchorXPx;
      const expandedReachPx = shellWidthPx - leftOffsetPx;
      const visibleRightEdgePx =
        clippingBounds.right - EXPANDED_PILL_EDGE_MARGIN_PX;
      const visibleLeftEdgePx =
        clippingBounds.left + EXPANDED_PILL_EDGE_MARGIN_PX;
      const availableRightPx = visibleRightEdgePx - anchorViewportXPx;
      const availableLeftPx = anchorViewportXPx - visibleLeftEdgePx;
      const rightFits = availableRightPx >= expandedReachPx;
      const leftFits = availableLeftPx >= expandedReachPx;

      setExpandDirection(
        !rightFits && (leftFits || availableLeftPx > availableRightPx)
          ? 'left'
          : 'right',
      );
    };

    updateExpandDirection();

    const shellElement = shellRef.current;
    const parentElement = shellElement?.offsetParent;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            updateExpandDirection();
          })
        : null;

    if (shellElement) resizeObserver?.observe(shellElement);
    if (parentElement instanceof HTMLElement) resizeObserver?.observe(parentElement);

    window.addEventListener('resize', updateExpandDirection);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateExpandDirection);
    };
  }, [leftOffsetPx, showLabel, x]);

  return (
    <div
      className={`absolute -translate-y-1/2 ${
        showLabel ? 'z-20' : 'z-10 hover:z-20'
      }`}
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <div
        ref={shellRef}
        className={`group flex items-center rounded-full border shadow-sm backdrop-blur-sm transition-[transform,colors] ${
          expandsLeft ? 'flex-row-reverse' : ''
        } ${shellClassName}`}
        style={{ transform: shellTransform }}
        onPointerEnter={() => {
          setIsHovered(true);
          onHoverChange?.(roleName);
        }}
        onPointerLeave={() => {
          setIsHovered(false);
          onHoverChange?.(null);
        }}
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
          disabled={!assignmentEnabled}
          aria-pressed={isPending}
          aria-label={tooltipText}
          className={`flex items-center rounded-full ${
            expandsLeft ? 'flex-row-reverse' : ''
          } ${
            assignmentEnabled
              ? 'cursor-pointer'
              : 'cursor-not-allowed opacity-60'
          }`}
          style={{
            padding: 0,
          }}
        >
          <span
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-white"
            style={{
              width: outerIndicatorDiameterPx,
              height: outerIndicatorDiameterPx,
            }}
          >
            {isAssigned ? (
              <RoleAssignmentIndicator
                assigned
                confidence={confidence}
                size={innerIndicatorDiameterPx}
                surface="dark"
              />
            ) : (
              <span
                className="rounded-full bg-gray-30"
                style={{
                  width: innerIndicatorDiameterPx,
                  height: innerIndicatorDiameterPx,
                }}
                aria-hidden
              />
            )}
          </span>
          <span
            className={`overflow-hidden whitespace-nowrap transition-[max-width,max-height,opacity,margin] duration-200 ${
              showLabel
                ? `${expandsLeft ? 'mr-2 pl-2' : 'ml-2 pr-2'} max-h-4 opacity-100 ${labelMaxWidthClassName}`
                : 'max-h-0 max-w-0 opacity-0'
            }`}
          >
            <Text
              as="span"
              variant="studio/body/medium/xs"
              className={`${isAssigned ? 'text-primary' : 'text-secondary'} block truncate`}
            >
              {fullLabel}
            </Text>
          </span>
        </button>

        <Tooltip
          text="Unassign"
          side="top"
          inModal
          disabled={!clearVisible}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onClear?.();
            }}
            aria-label="Unassign"
            className={`overflow-hidden rounded-full p-0 transition-[max-width,max-height,opacity,margin,padding] duration-200 ${
              clearVisible
                ? expandsLeft
                  ? 'mr-0.5 ml-1 max-h-5 max-w-[20px] px-0 opacity-100'
                  : 'mr-1 ml-0.5 max-h-5 max-w-[20px] px-0 opacity-100'
                : 'm-0 max-h-0 max-w-0 opacity-0 pointer-events-none'
            } text-secondary hover:text-primary`}
          >
            <span className="inline-flex h-5 w-5 items-center justify-center">
              <CrossIcon className="h-3.5 w-3.5" />
            </span>
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
